import { useState } from 'react';

import { Check, Copy, KeyRound, ShieldAlert, UserCog } from 'lucide-react';

import type { AtsCandidate, IssuedAccount } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { useDateFormat } from '@/features/company/api';
import { formatDate } from '@/lib/dates';
import { NOTHING } from '@/lib/nothing';
import { useDebounced } from '@/lib/useDebounced';

import { useAtsCandidates, useIssueAccount, useResetAccount } from '../api';
import { ReferralDrawer } from '../components/ReferralDrawer';
import styles from './AtsCandidatesPage.module.css';

/** Copy to the clipboard, and say so for a moment. */
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="ghost"
      size="sm"
      icon={copied ? <Check size={14} /> : <Copy size={14} />}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        });
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

/**
 * The candidates who have finished screening, and their portal credentials.
 *
 * Membership is derived from a finished RSR screening. Nothing marks a candidate as
 * being in the ATS, so nothing can forget to.
 */
export function AtsCandidatesPage() {
  const dateFormat = useDateFormat();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);

  const candidates = useAtsCandidates(debounced);
  const issue = useIssueAccount();
  const reset = useResetAccount();

  /** The password, held only long enough to be read and copied. */
  const [issued, setIssued] = useState<{
    candidate: AtsCandidate;
    result: IssuedAccount;
  } | null>(null);
  /**
   * Set when something tries to dismiss the password dialog without the reader saying
   * they have the password.
   *
   * `Modal` closes on a bare mousedown anywhere on its scrim and on Escape from
   * anywhere in the document, and either gesture would drop the one copy of the
   * password there is: it is not stored on the server and nothing here caches it. The
   * reset path is the expensive one, because the server has already replaced the
   * password the candidate was using by the time the dialog appears, so losing the
   * replacement locks them out of the portal until somebody notices.
   */
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [viewing, setViewing] = useState<AtsCandidate | null>(null);

  /** Deliberate: the password goes out of existence here. */
  const dropPassword = () => {
    setIssued(null);
    setConfirmDrop(false);
  };

  const run = async (candidate: AtsCandidate, what: 'issue' | 'reset') => {
    setProblem(null);
    setWorking(candidate.id);
    try {
      const result = await (what === 'issue' ? issue : reset).mutateAsync(candidate.id);
      setIssued({ candidate, result });
    } catch (error) {
      setProblem(
        error instanceof ApiError ? error.message : 'That account could not be set up.',
      );
    } finally {
      setWorking(null);
    }
  };

  const missing = (candidates.data ?? []).filter((row) => row.account === null).length;

  return (
    <Page>
      <PageHeader
        title="ATS candidates"
        description="Everyone whose screening is finished, and their CRM ATS account details."
      />

      <Panel>
        <div className={styles.filters}>
          <Input
            type="search"
            placeholder="Search by name"
            aria-label="Search candidates"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          {candidates.data ? (
            <span className={styles.count}>
              <b>{candidates.data.length}</b>{' '}
              {candidates.data.length === 1 ? 'candidate' : 'candidates'}
            </span>
          ) : null}
        </div>
      </Panel>

      {problem ? (
        <Alert tone="danger">
          <b>{problem}</b>
        </Alert>
      ) : null}

      {missing > 0 ? (
        <Alert tone="warning">
          <b>
            {missing === 1
              ? '1 candidate has no account'
              : `${missing} candidates have no account`}
          </b>{' '}
          and their CRM ATS account setup is incomplete.
        </Alert>
      ) : null}

      {candidates.isPending ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading" />
        </div>
      ) : candidates.isError ? (
        <ErrorState
          message="The ATS list did not load."
          onRetry={() => {
            void candidates.refetch();
          }}
        />
      ) : candidates.data.length === 0 ? (
        <EmptyState
          icon={<UserCog size={20} />}
          title="Nobody here yet"
          body={
            debounced
              ? 'No candidate matches that search.'
              : 'Candidates arrive once their RSR screening is marked done.'
          }
        />
      ) : (
        <Panel flush>
          <TableWrap>
            <Table minWidth={720}>
              <thead>
                <Tr>
                  <Th label="Candidate" />
                  <Th label="Reference" />
                  <Th label="Username" />
                  <Th label="Password set" />
                  <Th label="Referrals" />
                  <Th label="" />
                </Tr>
              </thead>
              <tbody>
                {candidates.data.map((row) => (
                  <Tr key={row.id}>
                    <Td>{row.full_name}</Td>
                    <Td className={styles.mono}>{row.reference}</Td>
                    <Td className={styles.mono}>
                      {row.account ? (
                        row.account.username
                      ) : (
                        <span className={styles.none}>No account</span>
                      )}
                    </Td>
                    <Td className={styles.muted}>
                      {row.account
                        ? formatDate(row.account.password_set_at, dateFormat)
                        : NOTHING}
                    </Td>
                    <Td>
                      {/* Two numbers rather than a percentage: "2 of 5" says what to
                          do next and "40%" does not. */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setViewing(row);
                        }}
                      >
                        {row.referrals_total === 0
                          ? 'Add jobs'
                          : `${row.referrals_shared} of ${row.referrals_total} shared`}
                      </Button>
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        variant={row.account ? 'ghost' : 'subtle'}
                        icon={<KeyRound size={14} />}
                        loading={working === row.id}
                        onClick={() => {
                          void run(row, row.account ? 'reset' : 'issue');
                        }}
                      >
                        {row.account ? 'Reset password' : 'Create account'}
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      )}

      {viewing ? (
        <ReferralDrawer
          candidate={viewing}
          onClose={() => {
            setViewing(null);
          }}
        />
      ) : null}

      {issued ? (
        <Modal
          open
          onClose={() => {
            setConfirmDrop(true);
          }}
          title="Copy this password now"
          description={`For ${issued.candidate.full_name}.`}
          icon={<ShieldAlert size={19} />}
          footer={
            confirmDrop ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirmDrop(false);
                  }}
                >
                  Keep it open
                </Button>
                <Button variant="danger" onClick={dropPassword}>
                  Close without it
                </Button>
              </>
            ) : (
              <Button onClick={dropPassword}>I have copied it</Button>
            )
          }
        >
          {/* The dismissal, turned into a question rather than obeyed. The password
              stays on screen underneath it, so the answer to "wait, I have not copied
              it" is to copy it now. */}
          {confirmDrop ? (
            <Alert tone="danger" className={styles.dismissPrompt}>
              <b>Close without the password?</b> It cannot be shown again, and{' '}
              {issued.candidate.full_name} cannot sign in until a new one is issued.
            </Alert>
          ) : null}

          {/* Said plainly and before the password, not as a footnote after it. Once
              this dialog closes the password is genuinely gone: it is not stored
              anywhere, on the server or here. */}
          <Alert tone="warning">
            <b>This is the only time this password will be shown.</b> It is not stored anywhere
            and cannot be looked up later. If it is lost, reset it and share the new one.
          </Alert>

          <dl className={styles.credentials}>
            <div className={styles.credential}>
              <dt className={styles.credentialLabel}>Username</dt>
              <dd className={styles.credentialValue}>
                <code>{issued.result.account.username}</code>
                <CopyButton value={issued.result.account.username} label="Copy" />
              </dd>
            </div>
            <div className={styles.credential}>
              <dt className={styles.credentialLabel}>Password</dt>
              <dd className={styles.credentialValue}>
                <code>{issued.result.password}</code>
                <CopyButton value={issued.result.password} label="Copy" />
              </dd>
            </div>
          </dl>
        </Modal>
      ) : null}
    </Page>
  );
}
